/**
 * Flow SVG Renderer
 *
 * Emits a self-contained inline SVG fragment for a {@link ServiceGraph}.
 * Used exclusively by the HTML migration guide to enable interactive
 * pan / zoom / click-to-jump behavior. The PDF and DOCX outputs
 * continue to embed the existing rasterised PNG produced by
 * {@link ./flow-renderer.ts}.
 *
 * Design notes:
 *  - All geometry comes from {@link ./flow-layout.ts} so SVG and PNG
 *    layouts are pixel-equivalent.
 *  - All nodes/edges live inside a single `<g class="vma-flow__viewport">`
 *    — the only element the client-side pan/zoom code mutates (one
 *    `transform` attribute write per frame).
 *  - Per-node `data-anchor` carries the document anchor ID of the
 *    matching component card so clicks can scroll to it.
 *  - Per-node `data-status` / `data-type` enable CSS-driven dimming
 *    when the toolbar's status / type filters are active.
 *  - Output is HTML-injection safe: all text content and attribute
 *    values flow through {@link escapeXml} / {@link escapeAttr}.
 */

import type { ServiceGraph } from '../types/graph.js';
import {
  ARROW_SIZE,
  FONT_SIZE,
  NODE_COLORS,
  NODE_HEIGHT,
  computeDimensions,
  computeLayout,
} from './flow-layout.js';

/** Optional values that customise the emitted SVG. */
export interface SvgRenderOptions {
  /** Highlight one node with a red outline (matches PNG behavior). */
  highlightNodeId?: string;
  /**
   * Map of node id → anchor id for the corresponding component detail
   * section in the HTML guide. When present, the node element gets a
   * `data-anchor` attribute clicks can use for navigation.
   */
  anchorMap?: Record<string, string>;
  /**
   * Stable id prefix for the marker / clip definitions so multiple
   * inline SVGs on the same page don't collide.
   */
  idPrefix?: string;
}

/** Result of an SVG render. */
export interface SvgRenderResult {
  svg: string;
  width: number;
  height: number;
  nodeCount: number;
}

const FILTER_DIAMOND_KIND = 'filter';
// Blue marks the focus node in a "Node Neighbor(s)" diagram, so it doesn't read
// as a problem with that component (red is reserved for manual-work flags).
const NEIGHBOR_HIGHLIGHT = '#0079C1';

/**
 * Renders the given service graph as an inline SVG string. Returns
 * `null` only if the graph has no nodes (the caller is expected to
 * fall back to a non-diagram representation in that case).
 */
export function renderFlowDiagramSvg(
  graph: ServiceGraph,
  options: SvgRenderOptions = {},
): SvgRenderResult | null {
  const layout = computeLayout(graph);
  if (layout.length === 0) return null;

  const dims = computeDimensions(layout);
  const width = dims.width;
  const height = dims.height;
  const idPrefix = options.idPrefix ?? `vmaflow-${Math.random().toString(36).slice(2, 8)}`;
  const arrowMarkerId = `${idPrefix}-arrow`;

  const nodeMap = new Map<string, (typeof layout)[number]>();
  for (const ln of layout) nodeMap.set(ln.node.id, ln);

  // --- Edges ---
  const edgeParts: string[] = [];
  for (const edge of graph.edges) {
    const from = nodeMap.get(edge.from);
    const to = nodeMap.get(edge.to);
    if (!from || !to) continue;
    const x1 = from.x + from.width;
    const y1 = from.y + NODE_HEIGHT / 2;
    const x2 = to.x;
    const y2 = to.y + NODE_HEIGHT / 2;
    edgeParts.push(
      `<line class="vma-flow__edge" x1="${fmt(x1)}" y1="${fmt(y1)}" x2="${fmt(x2)}" y2="${fmt(y2)}" stroke="#666666" stroke-width="1.5" marker-end="url(#${arrowMarkerId})"></line>`,
    );
  }

  // --- Nodes ---
  const nodeParts: string[] = [];
  for (const ln of layout) {
    const colors = NODE_COLORS[ln.node.type] ?? NODE_COLORS.processor;
    const x = ln.x;
    const y = ln.y;
    const w = ln.width;
    const cx = x + w / 2;
    const cy = y + NODE_HEIGHT / 2;
    const isFilter = ln.node.type === FILTER_DIAMOND_KIND;
    const isHighlighted = options.highlightNodeId && ln.node.id === options.highlightNodeId;
    const anchorId = options.anchorMap?.[ln.node.id];

    const shape = isFilter
      ? `<polygon points="${fmt(cx)},${fmt(cy - NODE_HEIGHT / 2)} ${fmt(cx + w / 2)},${fmt(cy)} ${fmt(cx)},${fmt(cy + NODE_HEIGHT / 2)} ${fmt(cx - w / 2)},${fmt(cy)}" fill="${colors.fill}" stroke="${colors.stroke}" stroke-width="2"></polygon>`
      : `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(w)}" height="${fmt(NODE_HEIGHT)}" fill="${colors.fill}" stroke="${colors.stroke}" stroke-width="2"></rect>`;

    const highlight = isHighlighted
      ? isFilter
        ? `<polygon class="vma-flow__node-highlight" points="${fmt(cx)},${fmt(cy - NODE_HEIGHT / 2 - 3)} ${fmt(cx + w / 2 + 3)},${fmt(cy)} ${fmt(cx)},${fmt(cy + NODE_HEIGHT / 2 + 3)} ${fmt(cx - w / 2 - 3)},${fmt(cy)}" fill="none" stroke="${NEIGHBOR_HIGHLIGHT}" stroke-width="3"></polygon>`
        : `<rect class="vma-flow__node-highlight" x="${fmt(x - 3)}" y="${fmt(y - 3)}" width="${fmt(w + 6)}" height="${fmt(NODE_HEIGHT + 6)}" fill="none" stroke="${NEIGHBOR_HIGHLIGHT}" stroke-width="3"></rect>`
      : '';

    const label = ln.node.label;
    const text = `<text x="${fmt(cx)}" y="${fmt(cy)}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-size="${FONT_SIZE}" fill="#333333" pointer-events="none">${escapeXml(label)}</text>`;

    const dataAttrs = [
      `data-id="${escapeAttr(ln.node.id)}"`,
      `data-type="${escapeAttr(ln.node.type)}"`,
    ];
    if (anchorId) dataAttrs.push(`data-anchor="${escapeAttr(anchorId)}"`);

    const accessible = anchorId
      ? ` role="link" tabindex="0" aria-label="${escapeAttr(`${label} (jump to details)`)}"`
      : ` aria-label="${escapeAttr(label)}"`;

    nodeParts.push(
      `<g class="vma-flow__node${anchorId ? ' is-clickable' : ''}" ${dataAttrs.join(' ')}${accessible}>${shape}${highlight}${text}<title>${escapeXml(label)}</title></g>`,
    );
  }

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" class="vma-flow__svg" viewBox="0 0 ${fmt(width)} ${fmt(height)}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Service flow diagram with ${graph.nodes.length} nodes">`,
    `<defs>`,
    `<marker id="${arrowMarkerId}" viewBox="0 0 ${ARROW_SIZE * 2} ${ARROW_SIZE * 2}" refX="${ARROW_SIZE * 1.5}" refY="${ARROW_SIZE}" markerWidth="${ARROW_SIZE * 1.5}" markerHeight="${ARROW_SIZE * 1.5}" orient="auto-start-reverse">`,
    `<path d="M0,0 L${ARROW_SIZE * 1.5},${ARROW_SIZE} L0,${ARROW_SIZE * 2} Z" fill="#666666"></path>`,
    `</marker>`,
    `</defs>`,
    `<g class="vma-flow__viewport" transform="translate(0,0) scale(1)">`,
    ...edgeParts,
    ...nodeParts,
    `</g>`,
    `</svg>`,
  ].join('');

  return { svg, width, height, nodeCount: graph.nodes.length };
}

/**
 * Format a numeric coordinate with up to 2 decimals and no trailing
 * zeros, keeping the SVG markup compact and deterministic.
 */
function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toString();
}

/**
 * Escapes a string for safe insertion inside an XML/SVG text node.
 */
function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escapes a string for safe insertion inside a double-quoted XML
 * attribute.
 */
function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
